import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Geofence, geofences } from '../api';
import { FleetMap } from '../components/FleetMap';
import { Link } from '../components/Links';

// Size in words: hectares under 1 km², else km².
export const areaText = (m2: number) => (m2 < 1_000_000
  ? `${(m2 / 10_000).toLocaleString('en-IE', { maximumFractionDigits: m2 < 100_000 ? 2 : 1 })} ha`
  : `${(m2 / 1_000_000).toLocaleString('en-IE', { maximumFractionDigits: 1 })} km²`);
export const shapeText = (g: Pick<Geofence, 'kind' | 'circle' | 'points'>) => (g.kind === 'circle' && g.circle
  ? `Circle, ${g.circle.radiusM.toLocaleString('en-IE')} m` : `Polygon, ${g.points.length} corners`);

export function Swatch({ colour }: { colour: string }) {
  return <span className="geo-dot" aria-hidden="true" style={{ '--geo': `var(--geofence-${colour})` } as CSSProperties} />;
}

// The customer's geofences: a map of them above a list, with a search.
export function Geofences() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Geofence[] | null>(null);
  const [q, setQ] = useState('');
  const [error, setError] = useState('');
  useEffect(() => { geofences.list().then((r) => setRows(r.geofences)).catch((e) => setError(e.message)); }, []);
  const shown = useMemo(() => (rows ?? []).filter((g) => g.name.toLowerCase().includes(q.trim().toLowerCase())), [rows, q]);

  return (
    <div className="page wide">
      <div className="page-head">
        <h1>Geofences</h1>
        <button className="primary" onClick={() => navigate('/geofences/new')}>Add geofence</button>
      </div>
      <p className="hint">Draw areas such as your yard or customers' sites to see when vehicles arrive and leave. A vehicle inside one shows its name above the address.</p>
      {error && <p className="error" role="alert">{error}</p>}
      {rows && rows.length > 0 && (
        <div className="toolbar">
          <label className="sr-only" htmlFor="geo-search">Search</label>
          <input id="geo-search" type="search" placeholder="Search geofences…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      )}
      <div className="geo-list-map card">
        <FleetMap geofences={shown} fitKey={`${q}|${rows?.length ?? -1}`} ready={rows !== null} />
      </div>
      {rows && rows.length === 0 && <p className="muted">No geofences yet. Add one to see when vehicles arrive and leave.</p>}
      {rows && rows.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Name</th><th className="geo-col-shape">Shape</th><th>Size</th><th title="Vehicles inside now">Inside</th></tr></thead>
            <tbody>{shown.map((g) => (
              <tr key={g.id}>
                <td><Link to={`/geofences/${g.id}`}><Swatch colour={g.colour} /><strong>{g.name}</strong></Link></td>
                <td className="geo-col-shape">{shapeText(g)}</td>
                <td className="nowrap">{areaText(g.areaM2)}</td>
                <td>{g.insideNow}</td>
              </tr>
            ))}</tbody>
          </table>
          {shown.length === 0 && <p className="muted" style={{ padding: '0 12px' }}>No geofences match.</p>}
        </div>
      )}
    </div>
  );
}
